import { Routes } from '@angular/router'
import { authenticatedGuard, eparakstsAuthGuard, nonAuthenticatedGuard, redirectAuthClientToProfileGuard, turnOnBusinessModeGuard } from '@features/guards'
import { TemplateSlug } from '@features/templating/contracts'
import { environment } from '@env'
import { ProjectName } from '@app/env-registry'
import { FlowWrapperComponent } from "@features/client-flow/flow-wrapper/flow-wrapper.component"

export const enum APP_ROUTE_NAMES {
  LOGIN = 'login',
  FLOW = 'flow',
  PROFILE = 'profile',
  LEAD = 'lead',
  PASSWORD_RECOVERY = 'password-recovery',
  FOOTER_AGREEMENT = 'agreement-document',
  FOOTER_ACCESSIBILITY_STATEMENT = 'accessibility-statement',
  FOOTER_PRIVACY_POLICY = 'privacy-policy',
  FOOTER_LICENSE = 'license',
  FOOTER_CONTACTS = 'contacts',
  FOOTER_PRICING = 'pricing',
  FOOTER_FAQ = 'faq',
  FOOTER_ABOUT = 'about-us',
  FOOTER_COOKIES_POLICY = 'cookies-policy',
}

export const enum FLOW_ROUTES {
  REGISTRATION = 'registration',
  LOAN = 'loan',
}

const dynamicTemplate = () => import('@features/dynamic-template-page/snapshot-slug-template-content/snapshot-slug-template-content.component')
  .then(m => m.SnapshotSlugTemplateContentComponent)

export const routes: Routes = [
  {
    path: APP_ROUTE_NAMES.FLOW,
    component: FlowWrapperComponent,
    children: [
      {
        path: FLOW_ROUTES.LOAN,
        loadChildren: () => import('@pages/loan-application-flow/routes'),
        title: $localize`:page-title|Application flow page title:Take Loan`,
        canActivateChild: [authenticatedGuard],
      },
      {
        path: FLOW_ROUTES.REGISTRATION,
        loadChildren: () => import('@pages/client-registration-flow/routes'),
        title: $localize`:page-title|Client flow page title:Registration`,
      }
    ],
  },
  {
    path: APP_ROUTE_NAMES.LOGIN,
    children: [
      {
        path: '',
        redirectTo: 'smart_id',
        pathMatch: 'full'
      },
      {
        path: ':tab',
        loadComponent: () => import('@pages/login/login.page').then(m => m.LoginPage),
        title: $localize`:page-title|Login page title:Login`,
        runGuardsAndResolvers: 'pathParamsChange',
      },
    ],
    canActivate: [nonAuthenticatedGuard, eparakstsAuthGuard],
  },
  {
    path: APP_ROUTE_NAMES.PASSWORD_RECOVERY,
    loadChildren: () => import('@pages/password-recovery/routes'),
    title: $localize`:page-title|Password recovery page title:Password Recovery`,
    canActivateChild: [nonAuthenticatedGuard],
  },
  {
    path: APP_ROUTE_NAMES.PROFILE,
    loadChildren: () => import('@pages/profile/routes'),
    canActivateChild: [authenticatedGuard],
  },
  {
    path: APP_ROUTE_NAMES.LEAD,
    loadChildren: () => import('@pages/lead-landing/routes'),
    canActivate: [eparakstsAuthGuard],
  },
  {
    path: APP_ROUTE_NAMES.FOOTER_AGREEMENT,
    loadComponent: dynamicTemplate,
    title: $localize`:page-title|Agreement condition page title:Agreement`,
    data: { templateSlug: 'doc-agreement-conditions' as TemplateSlug },
  },
  {
    path: APP_ROUTE_NAMES.FOOTER_ACCESSIBILITY_STATEMENT,
    loadComponent: dynamicTemplate,
    title: $localize`:page-title|Accessibility statement page title:Accessibility statement`,
    data: { templateSlug: 'doc-accessibility-statement' as TemplateSlug },
  },
  {
    path: APP_ROUTE_NAMES.FOOTER_PRIVACY_POLICY,
    loadComponent: dynamicTemplate,
    title: $localize`:page-title|Privacy policy page title:Privacy Policy`,
    data: { templateSlug: 'doc-privacy-policy' as TemplateSlug },
  },
  {
    path: APP_ROUTE_NAMES.FOOTER_COOKIES_POLICY,
    loadComponent: dynamicTemplate,
    title: $localize`:page-title|Cookie policy page title:Cookie Policy`,
    data: { templateSlug: 'doc-cookie-policy' as TemplateSlug },
  },
  {
    path: APP_ROUTE_NAMES.FOOTER_LICENSE,
    loadComponent: () => import('@pages/license/license.page').then(m => m.LicensePage),
    title: $localize`:page-title|License page title:License`,
  },
  {
    path: APP_ROUTE_NAMES.FOOTER_CONTACTS,
    loadComponent: () => import('@pages/contacts/contacts.page').then(m => m.ContactsPage),
    title: $localize`:page-title|Contacts page title:Contacts`,
  },
  {
    path: APP_ROUTE_NAMES.FOOTER_PRICING,
    loadComponent: () => import('@pages/pricing/pricing.page').then(m => m.PricingPage),
    title: $localize`:page-title|Pricing page title:Pricing`,
  },
  {
    path: APP_ROUTE_NAMES.FOOTER_ABOUT,
    loadComponent: () => import('@pages/about/about.page').then(m => m.AboutPage),
    title: $localize`:page-title|About us page title:About Us`,
  },
  {
    path: APP_ROUTE_NAMES.FOOTER_FAQ,
    loadComponent: () => import('@pages/faq/faq.page').then(m => m.FaqPage),
    title: $localize`:page-title|FAQ page title:FAQ`,
  },
  { path: 'bizness', canActivate: [turnOnBusinessModeGuard], children: [] },
  {
    path: '',
    pathMatch: 'full',
    loadComponent: () => {
      if (environment.project === ProjectName.FINEA) {
        return import(`@pages/home/finea/home.page`).then(m => m.HomePage)
      }

      if (environment.project === ProjectName.DALIDALI) {
        return import(`@pages/home/dalidali/home.page`).then(m => m.HomePage)
      }

      if (environment.project === ProjectName.MINIBANK) {
        return import(`@pages/home/minibank/home.page`).then(m => m.HomePage)
      }

      throw new Error('Unknown project')
    },
    canMatch: [redirectAuthClientToProfileGuard]
  },
  // Fallback when no prior route is matched
  { path: '**', redirectTo: '', pathMatch: 'full' },
]
